import React, { useEffect, useRef, useState } from 'react';
import { Input, InputRef } from 'antd';
import { BankOutlined } from '@ant-design/icons';
import { useIntl } from 'umi';
import { F } from '@/utils/intl';
import styles from './index.module.less';

type CompanyNameRowProps = {
  value?: string | null;
  /** Switching to company: the name is still missing, so focus it and require it. */
  pending?: boolean;
  onSave: (name: string) => void;
  /** Escape while switching: back to person. */
  onCancel?: () => void;
  /** Overrides the "Company" label (and placeholder), e.g. for a household. */
  label?: string;
  icon?: React.ReactNode;
  /** Not required: no required mark. */
  optional?: boolean;
};

/** The company of an organization customer (or a household's name) — shared by all of its contacts. */
const CompanyNameRow: React.FC<CompanyNameRowProps> = ({
  value,
  pending,
  onSave,
  onCancel,
  label,
  icon = <BankOutlined />,
  optional,
}) => {
  const intl = useIntl();
  const inputRef = useRef<InputRef>(null);
  const [draft, setDraft] = useState(value || '');
  const [missing, setMissing] = useState(false);

  useEffect(() => setDraft(value || ''), [value]);
  useEffect(() => {
    if (pending) inputRef.current?.focus();
  }, [pending]);

  const commit = () => {
    const name = draft.trim();
    if (!name) {
      // An organization needs its name (the backend refuses one without): a
      // pending switch asks for it, a cleared saved name simply comes back.
      if (pending) setMissing(true);
      else setDraft(value || '');
      return;
    }
    setMissing(false);
    if (pending || name !== (value || '')) onSave(name);
  };

  return (
    <label className={styles.companyRow}>
      <span className={styles.companyLabel}>
        {!optional && <span className={styles.requiredMark}>* </span>}
        {label || <F id="component.contacts.company" defaultMessage="Company" />}
      </span>
      <Input
        ref={inputRef}
        // Underlined like every other field on the card.
        variant="underlined"
        value={draft}
        status={missing ? 'error' : undefined}
        prefix={icon}
        placeholder={label || intl.formatMessage({ id: 'component.contacts.company', defaultMessage: 'Company' })}
        onChange={(event) => {
          setDraft(event.target.value);
          if (event.target.value.trim()) setMissing(false);
        }}
        onBlur={commit}
        onPressEnter={commit}
        onKeyDown={(event) => {
          if (event.key !== 'Escape') return;
          // Escape undoes this field, it doesn't close the project drawer.
          event.stopPropagation();
          setMissing(false);
          if (pending) onCancel?.();
          else setDraft(value || '');
        }}
      />
    </label>
  );
};

export default React.memo(CompanyNameRow);
